import type { AccessLevelProp, BadgeProps, OverlayTriggerState } from '@meterup/atto';
import type { FormEvent, ReactNode } from 'react';
import {
  Button,
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DropdownMenuGroup,
  DropdownMenuItem,
  DropdownMenuRadioGroup,
  DropdownMenuRadioItem,
  FeatureBadge,
  HStack,
  isAccessLevelVisible,
  PrimaryField,
  styled,
  SummaryList,
  TextInput,
  useDialogState,
  UtilityControl,
  VStack,
} from '@meterup/atto';
import { type DurationLike, DateTime, Duration, Interval } from 'luxon';
import { createContext, useContext, useMemo, useState } from 'react';

import type { DateTimeRangeState } from './DateTimeRangePicker';
import { DestructiveDialog } from '../Dialogs/DestructiveDialog';
import { notify } from '../Notifications';
import { formatDateTimeRangeLength, TimePeriod } from './utils';

export interface SavedDateTimeRange {
  id: string;
  name: string;
  start: string;
  end: string;
}

export interface SavedDateTimeRangeBounds {
  start: string;
  end: string;
}

/** Persistence for saved ranges, supplied by the app. */
export interface SavedDateTimeRangesStore {
  ranges: SavedDateTimeRange[];
  accessLevel?: AccessLevelProp | null;
  pinnedId?: string | null;
  /** The current page's default, if the app can tell which page this is. */
  pageDefaultId?: string | null;
  add: (range: Omit<SavedDateTimeRange, 'id'>) => SavedDateTimeRange;
  rename: (id: string, name: string) => void;
  remove: (id: string) => void;
  setPinned: (id: string | null) => void;
  setPageDefault?: (id: string | null) => void;
}

export interface SavedDateTimeRangesContextValue {
  ranges: SavedDateTimeRange[];
  accessLevel?: AccessLevelProp | null;
  /** Pickers that can show it start here, over their own default. */
  pinnedRange: SavedDateTimeRange | null;
  /** Pickers on the current page start here when nothing is pinned. */
  pageDefaultRange: SavedDateTimeRange | null;
  save: (bounds: SavedDateTimeRangeBounds, onSaved?: (range: SavedDateTimeRange) => void) => void;
  rename: (range: SavedDateTimeRange) => void;
  remove: (range: SavedDateTimeRange) => void;
  setPinned: (range: SavedDateTimeRange | null) => void;
  setPageDefault?: (range: SavedDateTimeRange | null) => void;
}

const SavedDateTimeRangesContext = createContext<SavedDateTimeRangesContextValue | null>(null);

export function useSavedDateTimeRangesOrNull() {
  return useContext(SavedDateTimeRangesContext);
}

export function formatSavedDateTimeRange({ start, end }: SavedDateTimeRangeBounds): string {
  const interval = Interval.fromDateTimes(DateTime.fromISO(start), DateTime.fromISO(end));
  const thisYear = DateTime.now().year;
  const showYear = interval.start?.year !== thisYear || interval.end?.year !== thisYear;

  return interval.toLocaleString({
    year: showYear ? 'numeric' : undefined,
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
}

export function findSavedDateTimeRange(
  ranges: SavedDateTimeRange[],
  { value, startDate, endDate }: Pick<DateTimeRangeState, 'value' | 'startDate' | 'endDate'>,
): SavedDateTimeRange | null {
  if (value !== TimePeriod.Custom) return null;

  const start = Date.parse(startDate);
  const end = Date.parse(endDate);

  return (
    ranges.find((range) => {
      if (Date.parse(range.start) !== start) return false;

      const rangeEnd = Date.parse(range.end);
      // An in-progress range is applied with its end clamped to now.
      return rangeEnd === end || (rangeEnd > Date.now() && end < rangeEnd);
    }) ?? null
  );
}

export function savedDateTimeRangeUnavailableReason(
  range: SavedDateTimeRangeBounds,
  {
    maxDuration,
    relativeTimeDirection,
  }: { maxDuration?: DurationLike; relativeTimeDirection: 'future' | 'past' },
): string | null {
  const start = DateTime.fromISO(range.start);

  if (relativeTimeDirection === 'past' && start > DateTime.now()) {
    return `Starts ${start.toLocaleString(DateTime.DATETIME_MED)}.`;
  }

  if (maxDuration != null) {
    const limit = Duration.fromDurationLike(maxDuration);
    if (DateTime.fromISO(range.end).diff(start).toMillis() > limit.toMillis()) {
      return `Longer than this view's ${limit.toHuman()} limit.`;
    }
  }

  return null;
}

/**
 * The pinned range and page default a picker with these options can start on. Only pickers that
 * look back from now, allow custom ranges, and can fit the range take one, so column filters and
 * fixed ranges opt out.
 */
export function useSavedStartingDateTimeRanges({
  allowCustom,
  fixAnchorToNow,
  maxDuration,
  relativeTimeDirection,
}: {
  allowCustom: boolean | AccessLevelProp | null;
  fixAnchorToNow: boolean;
  maxDuration?: DurationLike;
  relativeTimeDirection: 'future' | 'past';
}): { pinnedRange: SavedDateTimeRange | null; pageDefaultRange: SavedDateTimeRange | null } {
  const saved = useContext(SavedDateTimeRangesContext);

  const usable = (range: SavedDateTimeRange | null | undefined) =>
    range &&
    allowCustom &&
    fixAnchorToNow &&
    relativeTimeDirection === 'past' &&
    !savedDateTimeRangeUnavailableReason(range, { maxDuration, relativeTimeDirection })
      ? range
      : null;

  return {
    pinnedRange: usable(saved?.pinnedRange),
    pageDefaultRange: usable(saved?.pageDefaultRange),
  };
}

const PINNED_BADGE: BadgeProps = {
  label: 'Pinned',
  icon: 'pin',
  arrangement: 'hidden-label',
  tooltipContents: 'Pinned: overrides page defaults until unpinned',
};

const PAGE_DEFAULT_BADGE: BadgeProps = {
  label: 'Default',
  icon: 'reserve',
  arrangement: 'hidden-label',
  tooltipContents: "This page's default",
};

function SavedDateTimeRangeItem({
  range,
  isPinned,
  isPageDefault,
  unavailableReason,
  onPin,
  onUnpin,
  onSetPageDefault,
  onClearPageDefault,
  onRename,
  onDelete,
}: {
  range: SavedDateTimeRange;
  isPinned: boolean;
  isPageDefault: boolean;
  unavailableReason: string | null;
  onPin: () => void;
  onUnpin: () => void;
  /** Absent when the current page can't hold a default. */
  onSetPageDefault?: () => void;
  onClearPageDefault: () => void;
  onRename: () => void;
  onDelete: () => void;
}) {
  return (
    <DropdownMenuRadioItem
      value={range.id}
      closeOnClick
      badge={[isPinned && PINNED_BADGE, isPageDefault && PAGE_DEFAULT_BADGE].filter(
        (badge) => badge !== false,
      )}
      description={formatSavedDateTimeRange(range)}
      disabled={unavailableReason != null}
      tooltip={unavailableReason}
      actions={
        <>
          <DropdownMenuGroup>
            {isPinned ? (
              <DropdownMenuItem onClick={onUnpin} icon="cross-circle" variant="negative">
                Unpin
              </DropdownMenuItem>
            ) : (
              <DropdownMenuItem
                onClick={onPin}
                icon="pin"
                tooltip="Start every page on this range, over any page defaults, until you unpin it."
              >
                Pin
              </DropdownMenuItem>
            )}
            {isPageDefault && (
              <DropdownMenuItem onClick={onClearPageDefault} icon="cross-circle" variant="negative">
                Remove as default
              </DropdownMenuItem>
            )}
            {!isPageDefault && onSetPageDefault && (
              <DropdownMenuItem
                onClick={onSetPageDefault}
                icon="reserve"
                tooltip="Start this page on this range whenever nothing is pinned."
              >
                Set as default
              </DropdownMenuItem>
            )}
          </DropdownMenuGroup>
          <DropdownMenuGroup>
            <DropdownMenuItem onClick={onRename} icon="pencil">
              Rename
            </DropdownMenuItem>
            <DropdownMenuItem onClick={onDelete} icon="trash-can" variant="negative">
              Delete
            </DropdownMenuItem>
          </DropdownMenuGroup>
        </>
      }
    >
      {range.name}
    </DropdownMenuRadioItem>
  );
}

/** The saved ranges section of the picker's presets tab. */
export function SavedDateTimeRangesMenuGroup({
  state,
  onApply,
  closeThen = (then) => then(),
}: {
  state: DateTimeRangeState;
  onApply: (range: SavedDateTimeRange) => void;
  /** Closes the picker before a dialog opens or a range applies, so neither leaves it stranded open. */
  closeThen?: (then: () => void) => void;
}) {
  const saved = useSavedDateTimeRangesOrNull();
  const { maxDuration, relativeTimeDirection } = state;
  const pinnedId = saved?.pinnedRange?.id;
  const pageDefaultId = saved?.pageDefaultRange?.id;

  const items = useMemo(
    () =>
      (saved?.ranges ?? [])
        .map((range) => ({
          range,
          unavailableReason: savedDateTimeRangeUnavailableReason(range, {
            maxDuration,
            relativeTimeDirection,
          }),
        }))
        .sort(
          (a, b) =>
            Number(a.unavailableReason != null) - Number(b.unavailableReason != null) ||
            Number(b.range.id === pinnedId) - Number(a.range.id === pinnedId) ||
            Number(b.range.id === pageDefaultId) - Number(a.range.id === pageDefaultId) ||
            Date.parse(b.range.start) - Date.parse(a.range.start),
        ),
    [saved?.ranges, pinnedId, pageDefaultId, maxDuration, relativeTimeDirection],
  );

  if (!saved || !state.allowCustom || items.length === 0) return null;

  const activeRange = findSavedDateTimeRange(saved.ranges, state);
  const { setPageDefault } = saved;
  return (
    <DropdownMenuRadioGroup
      label={
        <HStack align="center" spacing={4}>
          Saved
          {saved.accessLevel && isAccessLevelVisible(saved.accessLevel) && (
            <FeatureBadge type={saved.accessLevel} size="x-small" />
          )}
        </HStack>
      }
      controls={
        !activeRange && (
          <UtilityControl
            icon="plus"
            label="Save current range as preset…"
            onClick={() =>
              closeThen(() => saved.save({ start: state.startDate, end: state.endDate }, onApply))
            }
          />
        )
      }
      value={activeRange?.id ?? ''}
      onValueChange={(id: string) => {
        const range = saved.ranges.find((r) => r.id === id);
        if (range) closeThen(() => onApply(range));
      }}
    >
      {items.map(({ range, unavailableReason }) => (
        <SavedDateTimeRangeItem
          key={range.id}
          range={range}
          isPinned={range.id === pinnedId}
          isPageDefault={range.id === pageDefaultId}
          unavailableReason={unavailableReason}
          onPin={() => saved.setPinned(range)}
          onUnpin={() => saved.setPinned(null)}
          onSetPageDefault={setPageDefault && (() => setPageDefault(range))}
          onClearPageDefault={() => setPageDefault?.(null)}
          onRename={() => closeThen(() => saved.rename(range))}
          onDelete={() => closeThen(() => saved.remove(range))}
        />
      ))}
    </DropdownMenuRadioGroup>
  );
}

const SavedDateTimeRangeForm = styled('form', {});

function formatSavedDateTimeRangeBound(iso: string): string {
  return DateTime.fromISO(iso).toLocaleString(DateTime.DATETIME_MED_WITH_WEEKDAY);
}

function SavedDateTimeRangeNameForm({
  heading,
  initialName,
  bounds,
  submitLabel,
  onCancel,
  onSubmit,
}: {
  heading: string;
  initialName: string;
  bounds: SavedDateTimeRangeBounds;
  submitLabel: string;
  onCancel: () => void;
  onSubmit: (name: string) => void;
}) {
  const [name, setName] = useState(initialName);

  const handleSubmit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (name.trim()) onSubmit(name.trim());
  };

  return (
    <SavedDateTimeRangeForm onSubmit={handleSubmit}>
      <DialogHeader icon="save" heading={heading} />
      <DialogContent>
        <VStack spacing={16}>
          <SummaryList
            pairs={[
              { label: 'Start', value: formatSavedDateTimeRangeBound(bounds.start) },
              { label: 'End', value: formatSavedDateTimeRangeBound(bounds.end) },
              {
                label: 'Length',
                value: formatDateTimeRangeLength(
                  DateTime.fromISO(bounds.start),
                  DateTime.fromISO(bounds.end),
                ),
              },
            ]}
          />
          <PrimaryField
            label="Label"
            element={
              <TextInput value={name} onChange={setName} placeholder="e.g. Home opener" autoFocus />
            }
          />
        </VStack>
      </DialogContent>
      <DialogFooter
        end={
          <>
            <Button type="button" variant="secondary" onClick={onCancel}>
              Cancel
            </Button>
            <Button type="submit" variant="primary" disabled={!name.trim()}>
              {submitLabel}
            </Button>
          </>
        }
      />
    </SavedDateTimeRangeForm>
  );
}

type SavedDateTimeRangeRequest =
  | {
      kind: 'save';
      bounds: SavedDateTimeRangeBounds;
      onSaved?: (range: SavedDateTimeRange) => void;
    }
  | { kind: 'rename'; range: SavedDateTimeRange }
  | { kind: 'delete'; range: SavedDateTimeRange };

function SavedDateTimeRangeNameDialog({
  request,
  state,
  store,
}: {
  request: SavedDateTimeRangeRequest | null;
  state: OverlayTriggerState;
  store: SavedDateTimeRangesStore;
}) {
  if (request?.kind === 'delete') return null;

  const handleSubmit = (name: string) => {
    if (request?.kind === 'save') {
      const range = store.add({ name, ...request.bounds });
      request.onSaved?.(range);
      notify(`Saved ${name}.`, { variant: 'positive' });
    } else if (request?.kind === 'rename') {
      store.rename(request.range.id, name);
      notify(`Renamed ${request.range.name} to ${name}.`, { variant: 'positive' });
    }
    state.close();
  };

  return (
    <Dialog preset="narrow" state={state}>
      {request && (
        <SavedDateTimeRangeNameForm
          heading={request.kind === 'save' ? 'Save preset' : 'Rename preset'}
          initialName={request.kind === 'rename' ? request.range.name : ''}
          bounds={request.kind === 'rename' ? request.range : request.bounds}
          submitLabel={request.kind === 'save' ? 'Save' : 'Rename'}
          onCancel={state.close}
          onSubmit={handleSubmit}
        />
      )}
    </Dialog>
  );
}

/**
 * Makes saved ranges available to every `DateTimeRangePicker` below it. The save, rename, and
 * delete dialogs live here rather than in the picker, whose popover unmounts when it closes.
 */
export function SavedDateTimeRangesProvider({
  store,
  children,
}: {
  store: SavedDateTimeRangesStore | null;
  children: ReactNode;
}) {
  const [request, setRequest] = useState<SavedDateTimeRangeRequest | null>(null);
  const nameDialog = useDialogState();
  const deleteDialog = useDialogState();

  const value = useMemo<SavedDateTimeRangesContextValue | null>(() => {
    if (!store) return null;
    const { setPageDefault } = store;
    return {
      ranges: store.ranges,
      accessLevel: store.accessLevel,
      pinnedRange: store.ranges.find((range) => range.id === store.pinnedId) ?? null,
      pageDefaultRange: store.ranges.find((range) => range.id === store.pageDefaultId) ?? null,
      setPinned: (range) => {
        store.setPinned(range?.id ?? null);
        notify(range ? `${range.name} is pinned over every page.` : 'Range unpinned.', {
          variant: 'positive',
        });
      },
      setPageDefault:
        setPageDefault &&
        ((range) => {
          setPageDefault(range?.id ?? null);
          notify(
            range
              ? `${range.name} is now this page's default.`
              : "This page's default was removed.",
            { variant: 'positive' },
          );
        }),
      save: (bounds, onSaved) => {
        setRequest({ kind: 'save', bounds, onSaved });
        nameDialog.openFromMenu();
      },
      rename: (range) => {
        setRequest({ kind: 'rename', range });
        nameDialog.openFromMenu();
      },
      remove: (range) => {
        setRequest({ kind: 'delete', range });
        deleteDialog.openFromMenu();
      },
    };
  }, [store, nameDialog, deleteDialog]);

  return (
    <SavedDateTimeRangesContext.Provider value={value}>
      {children}
      {store && (
        <>
          <SavedDateTimeRangeNameDialog request={request} state={nameDialog.state} store={store} />
          <DestructiveDialog
            state={deleteDialog.state}
            label={request?.kind === 'delete' ? request.range.name : 'range'}
            onDelete={() => {
              if (request?.kind !== 'delete') return;
              store.remove(request.range.id);
              notify(`Deleted ${request.range.name}.`, { variant: 'positive' });
              deleteDialog.state.close();
            }}
          />
        </>
      )}
    </SavedDateTimeRangesContext.Provider>
  );
}
